import "./SpotifyConnect.scss";

import classNames from 'classnames';
import { useEffect, useState } from "react";
import { useCurrentUser } from "../../../UserProvider";
import { api } from "../../../utils/api";
import { useForm } from "../../../hooks/Form";

/**
 * @see https://developer.spotify.com/documentation/web-api/reference/search
 * 
 * @typedef {Object} Artist
 * 
 * @property {string} id
 * @property {string} name
 * @property {string[]} genres
 * @property {{ url: string, width: number, height: number }[]} images
 */

/**
 * @param {{ search: string, selectedResult: Artist|null, onResultSelect: (artist: Artist) => void }} props
 */
function SearchList({ search, selectedResult, onResultSelect }) {
  /** @type {[Artist[], React.SetStateAction<Artist[]>]} */
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const handleClick = (artist) => {
    onResultSelect && onResultSelect(artist);
  };

  const handleSearch = async (search) => {
    try {
      if (search.trim().length === 0) {
        setData([]);
        return;
      }

      setLoading(true);

      const { data } = await api.get(`/spotify_connect/search?q=${encodeURIComponent(search)}`, {
        validateStatus: (status) => status === 200
      });

      if (data.artists.total > 0) {
        setData(data.artists.items);
      }

    } catch (error) {
      console.error(error);

    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      handleSearch(search);
    }, 300);

    return () => {
      clearTimeout(timeout);
    };
  }, [search]);

  return (
    <div className="form__search search">
      <ul className="search__list">
        {loading && (
          <li className="search__item item">
            <p className="item__fallback">
              Loading...
            </p>
          </li>
        )}
        {!loading && data.map((item, index) => (
          <li className="search__item item" key={index}>
            <div style={{ backgroundImage: `url(${item?.images[0]?.url})` }} className="item__image" />
            <a href={item.external_urls.spotify} target="_blank" rel="noopener noreferrer" className="item__name">
              {item.name}
            </a>
            <button type="button" onClick={() => handleClick(item)} className={classNames("item__button button button--primary", { "button--fill": item.id === selectedResult?.id, "button--outline": item.id !== selectedResult?.id })}>
              {item.id === selectedResult?.id ? "Selected" : "Select"}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * @param {{ handleNext: (value: boolean) => void }} props
 */
export default function SpotifyConnect({ handleNext }) {
  const { setCurrentUser } = useCurrentUser();

  const { isLoading, setIsLoading, error, setError, values, setFieldValue } = useForm({
    initialValues: {
      search: "",
    },
  });

  /** @type {[Artist|null, React.SetStateAction<Artist|null>]} */
  const [selectedResult, setSelectedResult] = useState(null);

  /** @param {React.FormEvent<HTMLFormElement>} event */
  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      if (!selectedResult) {
        setError(true);
      }

      setIsLoading(true);

      const { data } = await api.post(`/spotify_connect`, { spotify_id: selectedResult.id }, {
        validateStatus: (status) => status === 201
      });

      setCurrentUser(prev => ({ ...prev, display_name: data.display_name }));

      handleNext(true);

    } catch {
      setError(true);

    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setError(false);
  }, [values, setError]);

  return (
    <div className="__spotify_connect">
      <div className="title">
        Do you have a Spotify profile?
      </div>
      <div className="subtitle">
        Connect your account now, and we will help you fill up the blanks.
      </div>
      <form onSubmit={handleSubmit} className="form">
        {selectedResult && (
          <div className="form__artist artist">
            <div style={{ backgroundImage: `url(${selectedResult.images[0]?.url})` }} className="artist__image" />
            <p className="artist__name">
              {selectedResult.name}
            </p>
          </div>
        )}
        {selectedResult && (
          <button type="submit" disabled={isLoading} className="form__button button button--spotify">
            {isLoading ? "Loading..." : `Connect "${selectedResult.name}"`}
          </button>
        )}
        <div className='text-field text-field--white form__field'>
          <label htmlFor="search" className='text-field__label'>
            Search for an artist
          </label>
          <input
            id="search"
            name="search"
            type="text"
            placeholder="Artist name"
            value={values.search}
            required
            onChange={(event) => setFieldValue("search", event.currentTarget.value)}
            className='text-field__input'
          />
        </div>
        <SearchList
          search={values.search}
          selectedResult={selectedResult}
          onResultSelect={(item) => setSelectedResult(item)}
        />
        {error && (
          <div className="form__error error">
            Something went wrong. Please try again.
          </div>
        )}
      </form>
    </div>
  );
}